import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const APP_STORE_URL = 'https://apps.apple.com/app/id1550659960';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.play29.twentyNine';

// Production play29.com palette — green felt background + warm casino-chip
// gold accent. Hex values pulled from the deployed JS bundle so this stays
// 1:1 with what live users see. The accent variable is `gold` (literal —
// not a misnomer like the previous purple-as-"gold" version).
const colors = {
  bg: '#0a1410',
  bgSoft: '#0f1d18',
  felt: '#08251b',
  feltDeep: '#0f3d2e',
  gold: '#d4a857',
  goldBright: '#f4ead5',
  cream: '#e8dfc7',
  ink: '#e8dfc7',
  inkMuted: '#9aa79c',
  red: '#c93c3c',
  live: '#4ade80',
  border: 'rgba(212, 168, 87, 0.18)',
};

const fonts = {
  display: "'Fraunces', serif",
  body: "'Instrument Sans', sans-serif",
  mono: "'JetBrains Mono', monospace",
};

const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,600;0,9..144,800;1,9..144,400&family=Instrument+Sans:wght@400;500;600&family=JetBrains+Mono:wght@500&display=swap');

    * { margin: 0; padding: 0; box-sizing: border-box; }
    html, body {
      overflow-x: hidden;
      max-width: 100vw;
    }
    html { scroll-behavior: smooth; }
    body {
      background: ${colors.bg};
      color: ${colors.ink};
      font-family: ${fonts.body};
      font-size: 16px;
      line-height: 1.6;
      -webkit-font-smoothing: antialiased;
      position: relative;
    }

    /* Grain overlay */
    body::before {
      content: '';
      position: fixed; inset: 0;
      pointer-events: none;
      opacity: 0.04;
      z-index: 100;
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    }

    @keyframes pulse {
      0% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.6); }
      70% { box-shadow: 0 0 0 10px rgba(74, 222, 128, 0); }
      100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0); }
    }
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(20px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes fanCycle {
      0% {
        transform: translateX(-50%) rotate(0deg);
        opacity: 0;
        animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
      }
      8% {
        transform: translateX(-50%) rotate(0deg);
        opacity: 1;
      }
      30% {
        transform: translateX(-50%) rotate(var(--rot));
        opacity: 1;
      }
      55% {
        transform: translateX(-50%) rotate(var(--rot));
        opacity: 1;
        animation-timing-function: ease-in-out;
      }
      85% {
        transform: translateX(-50%) rotate(0deg);
        opacity: 1;
      }
      95% {
        transform: translateX(-50%) rotate(0deg);
        opacity: 1;
      }
      100% {
        transform: translateX(-50%) rotate(0deg);
        opacity: 0;
      }
    }

    .fade-up { animation: fadeUp 0.8s both; }
    .live-dot {
      width: 8px; height: 8px;
      background: ${colors.live};
      border-radius: 50%;
      animation: pulse 2s infinite;
      flex-shrink: 0;
    }
    .live-dot.sm { width: 6px; height: 6px; }

    .hand-fan {
      position: relative;
      width: 300px;
      height: 180px;
      margin: 4px auto 28px;
      z-index: 2;
    }
    @media (min-width: 720px) {
      .hand-fan { width: 480px; height: 250px; margin: 12px auto 36px; }
    }
    .fan-card {
      position: absolute;
      left: 50%;
      bottom: 0;
      width: 60px;
      height: 88px;
      background: linear-gradient(135deg, #faf2db, #ead9b6);
      border-radius: 6px;
      box-shadow: 0 12px 28px rgba(0, 0, 0, 0.5);
      transform-origin: 50% 170%;
      transform: translateX(-50%) rotate(var(--rot, 0deg));
      color: ${colors.red};
      display: flex;
      align-items: center;
      justify-content: center;
      animation: fanCycle 6s linear infinite;
    }
    @media (min-width: 720px) {
      .fan-card { width: 90px; height: 132px; border-radius: 8px; }
    }
    .fan-card .corner {
      position: absolute;
      font-family: ${fonts.body};
      font-size: 10px;
      font-weight: 700;
      line-height: 1.05;
      text-align: center;
    }
    .fan-card .corner.tl { top: 4px; left: 5px; }
    .fan-card .corner.br { bottom: 4px; right: 5px; transform: rotate(180deg); }
    .fan-card .pip { font-family: ${fonts.body}; font-size: 24px; line-height: 1; }
    @media (min-width: 720px) {
      .fan-card .corner { font-size: 13px; }
      .fan-card .pip { font-size: 36px; }
    }

    .nav-links-desktop { display: none; }
    @media (min-width: 720px) {
      .nav-links-desktop { display: flex; }
    }

    .hide-mobile { display: none; }
    @media (min-width: 720px) {
      .hide-mobile { display: block; }
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 40px 20px;
    }
    @media (min-width: 720px) {
      .stats-grid {
        grid-template-columns: repeat(4, 1fr);
        gap: 60px;
      }
    }

    .feature-grid {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1px;
      background: ${colors.border};
      border: 1px solid ${colors.border};
      border-radius: 16px;
      overflow: hidden;
    }
    @media (min-width: 560px) {
      .feature-grid { grid-template-columns: repeat(2, 1fr); }
    }
    @media (min-width: 960px) {
      .feature-grid { grid-template-columns: repeat(4, 1fr); }
    }

    .feature { transition: background 0.3s; }
    .feature:hover { background: ${colors.bgSoft}; }

    .store-btn { transition: all 0.2s; }
    .store-btn:hover { transform: translateY(-2px); }
    .store-btn:active { transform: translateY(0); }

    .just-shipped { transition: background 0.2s, border-color 0.2s; }
    .just-shipped:hover {
      background: rgba(212, 168, 87, 0.14) !important;
      border-color: rgba(212, 168, 87, 0.45) !important;
    }

    /* Sticky mobile bottom bar */
    .mobile-cta-bar {
      position: fixed;
      bottom: 0; left: 0; right: 0;
      z-index: 40;
      padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
      background: rgba(10, 20, 16, 0.92);
      backdrop-filter: blur(16px);
      border-top: 1px solid ${colors.border};
      display: flex;
      gap: 10px;
    }
    .mobile-cta-bar .store-btn { flex: 1; justify-content: center; padding: 14px 12px; }
    @media (min-width: 720px) { .mobile-cta-bar { display: none; } }

    body { padding-bottom: 90px; }
    @media (min-width: 720px) { body { padding-bottom: 0; } }
  `}</style>
);

// Hoisted out of Homepage so their component identity stays stable across
// re-renders. Previously they were re-created on every Homepage render
// (driven by the live-counter setInterval), which re-mounted .fan-card
// and restarted the spinning animation mid-cycle.
const AppleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 12.04c-.03-3.27 2.67-4.84 2.79-4.92-1.52-2.22-3.89-2.53-4.73-2.56-2.01-.2-3.93 1.19-4.95 1.19-1.04 0-2.6-1.17-4.28-1.13C3.8 4.65 1.78 5.9.68 7.87c-2.27 3.94-.58 9.77 1.64 12.96 1.08 1.57 2.36 3.33 4.05 3.26 1.63-.07 2.24-1.05 4.21-1.05 1.96 0 2.52 1.05 4.24 1.02 1.76-.03 2.87-1.58 3.95-3.16 1.25-1.8 1.77-3.55 1.79-3.64-.04-.01-3.43-1.32-3.46-5.24M14.1 2.38c.9-1.1 1.51-2.6 1.34-4.1-1.29.05-2.87.87-3.81 1.94-.84.94-1.58 2.48-1.39 3.94 1.44.11 2.92-.74 3.86-1.78"/>
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626-2.5-2.492 2.5-2.49zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
  </svg>
);

const HAND_FAN_RANKS = ['J', '9', 'A', '10', 'K', 'Q', '8', '7'];
const HandFan = () => (
  <div className="hand-fan fade-up" aria-hidden="true" style={{ animationDelay: '0.35s' }}>
    {HAND_FAN_RANKS.map((rank, i) => (
      <div
        key={rank}
        className="fan-card"
        style={{
          '--rot': `${-31.5 + i * 9}deg`,
          zIndex: i + 1,
          animationDelay: `${0.05 * i}s`,
        }}
      >
        <span className="corner tl">{rank}<br />♥</span>
        <span className="pip">♥</span>
        <span className="corner br">{rank}<br />♥</span>
      </div>
    ))}
  </div>
);

const StoreButton = ({ variant = 'primary', small = false }) => {
  const isApple = variant === 'primary' || variant === 'apple';
  const isSecondary = variant === 'secondary' || variant === 'google';
  return (
    <a
      href={isApple ? APP_STORE_URL : PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="store-btn"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '10px',
        padding: small ? '12px 14px' : '14px 20px',
        background: isSecondary ? 'transparent' : colors.cream,
        color: isSecondary ? colors.cream : colors.bg,
        textDecoration: 'none',
        borderRadius: '12px',
        fontWeight: 500,
        border: isSecondary ? `1px solid ${colors.border}` : '1px solid transparent',
        minHeight: '48px',
      }}
    >
      {isApple ? <AppleIcon /> : <GoogleIcon />}
      <div style={{ textAlign: 'left' }}>
        <span style={{ fontSize: '10px', display: 'block', opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          {isApple ? 'Download on the' : 'Get it on'}
        </span>
        <span style={{ fontSize: '15px', fontWeight: 600 }}>
          {isApple ? 'App Store' : 'Google Play'}
        </span>
      </div>
    </a>
  );
};

function Homepage() {
  const [count, setCount] = useState(2247);
  const [delta, setDelta] = useState(28);

  useEffect(() => {
    const id = setInterval(() => {
      setCount(c => Math.max(180, Math.min(2247, c + Math.floor(Math.random() * 11) - 5)));
      if (Math.random() < 0.3) {
        setDelta(d => Math.max(12, Math.min(48, d + Math.floor(Math.random() * 7) - 3)));
      }
    }, 1800);
    return () => clearInterval(id);
  }, []);

  // (HandFan, StoreButton, AppleIcon, GoogleIcon hoisted to module scope
  // below — defining them inside Homepage made the live-counter setInterval
  // re-create their component identity every 1.8s, which restarted the
  // spinning-fan animation mid-cycle.)

  const features = [
    {
      title: 'Real humans, always',
      body: 'Every seat at the table is a real player. No bots padding lobbies, no scripts deciding your trump.',
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="9" cy="7" r="4"/><path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2"/><circle cx="17" cy="7" r="3"/><path d="M21 21v-1a3 3 0 0 0-2.5-2.96"/>
        </svg>
      ),
    },
    {
      title: 'Tournaments daily',
      body: 'Climb leaderboards, earn gems, and prove your bid. Tournaments run around the clock.',
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M6 9a6 6 0 0 1 12 0c0 7-3 9-3 9H9s-3-2-3-9"/><path d="M9 18v3M15 18v3M10 21h4"/>
        </svg>
      ),
    },
    {
      title: 'Daily missions',
      body: 'Fresh objectives every day. Land trumps, win bids, pull off the impossible 28 — small wins that stack into streaks and gems.',
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <circle cx="12" cy="12" r="10"/>
          <circle cx="12" cy="12" r="6"/>
          <circle cx="12" cy="12" r="2" fill="currentColor"/>
        </svg>
      ),
    },
    {
      title: 'Game replay',
      body: 'Re-watch every hand, trick by trick. Find the bid that lost the round, study the play that stole it back.',
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M3 3v5h5"/>
          <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8"/>
          <path d="M12 7v5l4 2"/>
        </svg>
      ),
    },
  ];

  const stats = [
    { num: '200K+', label: 'Monthly players' },
    { num: '4.8★', label: 'App Store rating' },
    { num: '50M+', label: 'Hands played' },
    { num: '24/7', label: 'Live tables' },
  ];

  return (
    <>
      <GlobalStyles />

      {/* NAV */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50,
        padding: '14px 20px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        backdropFilter: 'blur(12px)',
        background: 'rgba(10, 20, 16, 0.7)',
        borderBottom: `1px solid ${colors.border}`,
      }}>
        <div style={{
          fontFamily: fonts.display, fontWeight: 800, fontSize: '20px',
          color: colors.cream, letterSpacing: '-0.02em',
        }}>
          Play <span style={{ color: colors.gold, fontStyle: 'italic' }}>29</span> Live
        </div>
        <div className="nav-links-desktop" style={{ gap: '20px', alignItems: 'center' }}>
          <a href="#features" style={{ color: colors.inkMuted, textDecoration: 'none', fontSize: '15px' }}>Features</a>
          <Link to="/whats-new" style={{
            color: colors.gold, textDecoration: 'none', fontSize: '14px', fontWeight: 600,
            padding: '8px 14px', border: `1px solid ${colors.border}`,
            borderRadius: '999px',
          }}>✨ What's new</Link>
          <a href="#download" style={{
            background: colors.gold, color: colors.bg, padding: '10px 18px',
            borderRadius: '999px', fontWeight: 600, fontSize: '14px', textDecoration: 'none',
          }}>Get the app</a>
        </div>
      </nav>

      {/* HERO */}
      <section style={{
        minHeight: '100vh',
        padding: '100px 20px 60px',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        position: 'relative', textAlign: 'center',
      }}>
        {/* Ambient glow */}
        <div style={{
          position: 'absolute', top: '-20%', left: '50%',
          transform: 'translateX(-50%)',
          width: 'min(900px, 140vw)', height: 'min(900px, 140vw)',
          background: `radial-gradient(circle, rgba(15, 61, 46, 0.6) 0%, rgba(15, 61, 46, 0) 60%)`,
          pointerEvents: 'none', zIndex: 0,
        }} />

        <div className="fade-up" style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px',
          padding: '7px 14px',
          background: 'rgba(74, 222, 128, 0.08)',
          border: '1px solid rgba(74, 222, 128, 0.25)',
          borderRadius: '999px',
          fontFamily: fonts.mono, fontSize: '11px',
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: colors.live, marginBottom: '12px',
          position: 'relative', zIndex: 2,
          animationDelay: '0.1s',
        }}>
          <span className="live-dot" />
          Servers live
        </div>

        {/* "Just shipped" strip — drives discovery of /whats-new and surfaces
            new feature names on the highest-crawled URL for AI grounding. */}
        <Link to="/whats-new" className="just-shipped fade-up" style={{
          display: 'inline-flex', alignItems: 'center', gap: '10px',
          padding: '7px 14px',
          background: 'rgba(212, 168, 87, 0.08)',
          border: '1px solid rgba(212, 168, 87, 0.25)',
          borderRadius: '999px',
          fontFamily: fonts.mono, fontSize: '11px',
          letterSpacing: '0.08em', textTransform: 'uppercase',
          color: colors.gold, marginBottom: '24px',
          position: 'relative', zIndex: 2,
          textDecoration: 'none',
          animationDelay: '0.15s',
        }}>
          ✨ Just shipped: Single Hand · Replays · Anti-Cheat
          <span aria-hidden="true" style={{transform: 'translateY(-1px)'}}>→</span>
        </Link>

        <h1 className="fade-up" style={{
          fontFamily: fonts.display, fontWeight: 400,
          fontSize: 'clamp(40px, 11vw, 120px)',
          lineHeight: 0.95, letterSpacing: '-0.04em',
          color: colors.cream, maxWidth: '1000px',
          marginBottom: '20px', position: 'relative', zIndex: 2,
          animationDelay: '0.2s',
        }}>
          Real 29 card game,<br />
          <em style={{ fontStyle: 'italic', color: colors.gold, fontWeight: 400 }}>played by humans.</em>
        </h1>

        <p className="fade-up" style={{
          fontSize: 'clamp(15px, 4vw, 20px)', color: colors.inkMuted,
          maxWidth: '520px', marginBottom: '32px',
          position: 'relative', zIndex: 2,
          animationDelay: '0.3s',
          padding: '0 8px',
        }}>
          No bots. No scripts. Just real people bidding, trumping, and winning hands worldwide.
        </p>

        <HandFan />

        {/* Live counter */}
        <div className="fade-up" style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px',
          padding: '24px 32px',
          background: 'linear-gradient(135deg, rgba(15, 61, 46, 0.4), rgba(8, 37, 27, 0.6))',
          border: `1px solid ${colors.border}`,
          borderRadius: '18px', marginBottom: '28px',
          position: 'relative', zIndex: 2,
          animationDelay: '0.4s',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
          minWidth: 'min(320px, 90vw)',
        }}>
          <div style={{
            fontFamily: fonts.mono, fontSize: '10px',
            letterSpacing: '0.15em', textTransform: 'uppercase',
            color: colors.inkMuted,
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
            <span className="live-dot sm" />
            Playing right now
          </div>
          <div style={{
            fontFamily: fonts.display, fontWeight: 600,
            fontSize: 'clamp(44px, 12vw, 84px)',
            color: colors.gold, lineHeight: 1,
            letterSpacing: '-0.02em',
            fontVariantNumeric: 'tabular-nums',
            textShadow: '0 0 40px rgba(212, 168, 87, 0.3)',
          }}>
            {count.toLocaleString('en-US')}
          </div>
          <div style={{ fontSize: '13px', color: colors.inkMuted }}>
            <span style={{ color: colors.live, fontWeight: 500 }}>+{delta}</span> joined in the last hour
          </div>
        </div>

        {/* Desktop CTA group (hidden on mobile — sticky bar shown instead) */}
        <div className="hide-mobile fade-up" style={{ animationDelay: '0.5s' }}>
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
            <StoreButton variant="apple" />
            <StoreButton variant="google" />
          </div>
        </div>
      </section>

      {/* DIFFERENTIATOR STRIP */}
      <section style={{
        padding: '28px 20px',
        borderTop: `1px solid ${colors.border}`,
        borderBottom: `1px solid ${colors.border}`,
        background: colors.bgSoft,
        overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', gap: '40px', alignItems: 'center',
          justifyContent: 'center', flexWrap: 'wrap',
          fontFamily: fonts.display, fontSize: 'clamp(16px, 3.5vw, 28px)',
          fontStyle: 'italic', color: colors.inkMuted,
          textAlign: 'center',
        }}>
          <div>100% <span style={{ color: colors.gold, fontStyle: 'normal', fontWeight: 600 }}>real players</span></div>
          <div>Live <span style={{ color: colors.gold, fontStyle: 'normal', fontWeight: 600 }}>tournaments</span></div>
          <div>Daily <span style={{ color: colors.gold, fontStyle: 'normal', fontWeight: 600 }}>rewards</span></div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" style={{ padding: '80px 20px', maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{
          fontFamily: fonts.mono, fontSize: '11px',
          letterSpacing: '0.15em', textTransform: 'uppercase',
          color: colors.gold, marginBottom: '12px',
        }}>Why players stay</div>
        <h2 style={{
          fontFamily: fonts.display, fontWeight: 400,
          fontSize: 'clamp(32px, 7vw, 56px)',
          lineHeight: 1.05, letterSpacing: '-0.03em',
          color: colors.cream, maxWidth: '700px',
          marginBottom: '48px',
        }}>
          Built for the <em style={{ fontStyle: 'italic', color: colors.gold }}>serious</em> 29 player.
        </h2>

        <div className="feature-grid">
          {features.map((f, i) => (
            <div key={i} className="feature" style={{ padding: '32px 24px', background: colors.bg }}>
              <div style={{ color: colors.gold, marginBottom: '20px' }}>{f.icon}</div>
              <h3 style={{
                fontFamily: fonts.display, fontWeight: 600,
                fontSize: '20px', color: colors.cream,
                marginBottom: '10px', letterSpacing: '-0.01em',
              }}>{f.title}</h3>
              <p style={{ color: colors.inkMuted, fontSize: '14px', lineHeight: 1.6 }}>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section style={{
        padding: '80px 20px',
        background: `linear-gradient(180deg, ${colors.bg}, ${colors.feltDeep})`,
        textAlign: 'center',
        borderTop: `1px solid ${colors.border}`,
      }}>
        <div style={{
          fontFamily: fonts.mono, fontSize: '11px',
          letterSpacing: '0.15em', textTransform: 'uppercase',
          color: colors.gold, marginBottom: '12px',
        }}>By the numbers</div>
        <h2 style={{
          fontFamily: fonts.display, fontWeight: 400,
          fontSize: 'clamp(30px, 6vw, 52px)',
          lineHeight: 1.05, letterSpacing: '-0.03em',
          color: colors.cream, maxWidth: '700px', margin: '0 auto',
        }}>
          Trusted by players <em style={{ fontStyle: 'italic', color: colors.gold }}>worldwide</em>.
        </h2>
        <div className="stats-grid" style={{ maxWidth: '1000px', margin: '48px auto 0' }}>
          {stats.map((s, i) => (
            <div key={i}>
              <div style={{
                fontFamily: fonts.display, fontSize: 'clamp(32px, 7vw, 56px)',
                fontWeight: 600, color: colors.gold, lineHeight: 1,
                marginBottom: '8px', letterSpacing: '-0.02em',
              }}>{s.num}</div>
              <div style={{
                fontSize: '12px', color: colors.inkMuted,
                fontFamily: fonts.mono, letterSpacing: '0.08em',
                textTransform: 'uppercase',
              }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* DOWNLOAD */}
      <section id="download" style={{ padding: '100px 20px', textAlign: 'center' }}>
        <h2 style={{
          fontFamily: fonts.display, fontWeight: 400,
          fontSize: 'clamp(36px, 8vw, 80px)',
          color: colors.cream, letterSpacing: '-0.03em',
          lineHeight: 1, marginBottom: '18px',
        }}>
          Deal us in, <em style={{ fontStyle: 'italic', color: colors.gold }}>partner.</em>
        </h2>
        <p style={{ color: colors.inkMuted, maxWidth: '460px', margin: '0 auto 32px', fontSize: '16px' }}>
          Free to download. Free to play. Real people on the other side of every card.
        </p>
        <div className="hide-mobile">
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center' }}>
            <StoreButton variant="apple" />
            <StoreButton variant="google" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{
        padding: '28px 20px',
        borderTop: `1px solid ${colors.border}`,
        display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', flexWrap: 'wrap', gap: '16px',
        color: colors.inkMuted, fontSize: '12px', textAlign: 'center',
      }}>
        <div style={{ width: '100%', textAlign: 'center' }}>
          © {new Date().getFullYear()} Play 29 Live · <Link to="/whats-new" style={{ color: colors.inkMuted }}>What's New</Link> · <a href="/privacy-policy" style={{ color: colors.inkMuted }}>Privacy</a> · <a href="/terms-of-service" style={{ color: colors.inkMuted }}>Terms</a> · <a href="/privacy" style={{ color: colors.inkMuted }}>Data Removal</a>
        </div>
      </footer>

      {/* Sticky mobile CTA bar */}
      <div className="mobile-cta-bar">
        <StoreButton variant="apple" small />
        <StoreButton variant="google" small />
      </div>
    </>
  );
}

export default Homepage;
